import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import RoleSwitcher from '../RoleSwitcher';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  MessageSquare, 
  DollarSign,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Calendar,
  Award,
  Moon,
  Sun,
  Clock
} from 'lucide-react';
import { Button } from '../ui/button';

const DashboardLayout = ({ children, role, activeView, onViewChange, onRoleChange }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const { user, logout, activeRole } = useAuth();
  const navigate = useNavigate();
  
  // Use activeRole from context if available, otherwise fall back to prop
  const currentRole = activeRole || role;
  
  // Auto-open sidebar on large screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load dark mode preference from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMenuClick = (item) => {
    // Use internal view switching for all roles when onViewChange is provided
    if (onViewChange) {
      onViewChange(item.path);
    } else {
      // Fallback to React Router navigation
      navigate(item.path);
    }
    // Close sidebar on mobile after selection
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Handle role switch from RoleSwitcher component
  const handleRoleSwitch = (newRole) => {
    if (onRoleChange) {
      onRoleChange(newRole);
    }
  };

  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const roleMenus = {
    student: [
      { icon: LayoutDashboard, label: 'Dashboard', path: 'dashboard' },
      { icon: Calendar, label: 'My Classes', path: 'classes' },
      { icon: BookOpen, label: 'Quizzes', path: 'quizzes' },
      { icon: Award, label: 'Progress', path: 'progress' },
      { icon: MessageSquare, label: 'Messages', path: 'messages' }
    ],
    parent: [
      { icon: LayoutDashboard, label: 'Dashboard', path: 'dashboard' },
      { icon: Users, label: 'My Children', path: 'children' },
      { icon: Calendar, label: 'Schedule', path: 'schedule' },
      { icon: Clock, label: 'Timesheets', path: 'timesheets' },
      { icon: DollarSign, label: 'Billing', path: 'billing' },
      { icon: BookOpen, label: 'Progress Reports', path: 'reports' },
      { icon: MessageSquare, label: 'Messages', path: 'messages' }
    ],
    tutor: [
      { icon: LayoutDashboard, label: 'Dashboard', path: 'dashboard' },
      { icon: Calendar, label: 'My Schedule', path: 'schedule' },
      { icon: BookOpen, label: 'My Timesheets', path: 'timesheet' },
      { icon: Users, label: 'Students', path: 'students' },
      { icon: DollarSign, label: 'Earnings', path: 'earnings' },
      { icon: MessageSquare, label: 'Messages', path: 'messages' }
    ],
    admin: [
      { icon: LayoutDashboard, label: 'Dashboard', path: 'overview' },
      { icon: BookOpen, label: 'Tasks', path: 'tasks' },
      { icon: Calendar, label: 'Timesheets', path: 'timesheets' },
      { icon: Clock, label: 'Tutor Availability', path: 'availability' },
      { icon: DollarSign, label: 'Payouts', path: 'payout' },
      { icon: FileText, label: 'Invoices', path: 'invoices' },
      { icon: Users, label: 'Users', path: 'users' },
      { icon: BookOpen, label: 'Subjects', path: 'subjects' },
      { icon: MessageSquare, label: 'Messages', path: 'messages' }
    ]
  };

  const menuItems = roleMenus[currentRole] || roleMenus.student;

  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''}`}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        {/* Top Header - Match Landing Page Style */}
        <header className="fixed top-0 left-0 right-0 h-20 bg-gray-900/95 backdrop-blur-md shadow-lg z-40">
          <div className="flex items-center justify-between h-full px-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {sidebarOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
              </button>
              <img 
                src="/fourq_logo.svg" 
                alt="FourQ Learn" 
                className="h-16 w-auto cursor-pointer hover:opacity-80 transition-opacity" 
                onClick={() => navigate('/')}
                title="Go to Home"
              />
            </div>

            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {darkMode ? (
                  <Sun className="w-6 h-6 text-yellow-400" />
                ) : (
                  <Moon className="w-6 h-6 text-gray-300" />
                )}
              </button>

              <button className="relative p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Bell className="w-6 h-6 text-gray-300" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </button>

              {/* Role Switcher - Shows only if user has multiple roles */}
              <RoleSwitcher onRoleChange={handleRoleSwitch} />

              <div className="flex items-center gap-3 pl-4 border-l border-gray-600">
                <img 
                  src={user?.avatar} 
                  alt={user?.name}
                  className="w-10 h-10 rounded-full ring-2 ring-cyan-500"
                />
                <div className="hidden md:block">
                  <p className="text-sm font-semibold text-white">{user?.name}</p>
                  <p className="text-xs text-gray-400 capitalize">{currentRole}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Sidebar Backdrop (Mobile Only) */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeSidebarOnMobile}
              className="lg:hidden fixed inset-0 top-20 bg-black/50 z-20"
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ duration: 0.2 }}
              className="fixed left-0 top-20 bottom-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-30 transition-colors overflow-y-auto"
            >
              <div className="flex flex-col h-full p-4">
                <nav className="flex-1 space-y-2">
                  {menuItems.map((item, index) => {
                    const Icon = item.icon;
                    const isActive = activeView === item.path;
                    return (
                      <button
                        key={index}
                        onClick={() => handleMenuClick(item)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                          isActive 
                            ? 'bg-cyan-100 dark:bg-cyan-900 text-cyan-600 dark:text-cyan-400 font-semibold' 
                            : 'text-gray-700 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-gray-700 hover:text-cyan-600 dark:hover:text-cyan-400'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    onClick={() => {
                      onViewChange ? onViewChange('profile') : navigate('/profile');
                      closeSidebarOnMobile();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                  >
                    <Settings className="w-5 h-5" />
                    <span className="font-medium">My Profile</span>
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main 
          className={`pt-20 transition-all duration-200 ${
            sidebarOpen ? 'lg:pl-64' : 'pl-0'
          }`}
        >
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
